import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * <app-pagination>
 * Componente Standalone de paginación institucional de ABAQ Querétaro.
 * Réplica exacta del diseño, clases CSS y comportamiento de accreditation-list y my-accreditations.
 */
@Component({
  selector: 'app-pagination',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaginationComponent {
  /** Página actual (1-indexed). Admite two-way binding [(page)] */
  @Input() page: number = 1;

  /** Total de páginas calculado externamente (opcional si se pasa totalItems) */
  @Input() totalPages?: number;

  /** Total de registros para cálculo automático de totalPages */
  @Input() totalItems?: number;

  /** Cantidad de elementos por página (predeterminado: 10) */
  @Input() pageSize: number = 10;

  /** Máximo de botones numéricos visibles antes de condensar con elipsis */
  @Input() maxVisiblePages: number = 7;

  /**
   * Comportamiento del scroll al paginar:
   * - 'preserve': Mantiene la posición vertical exacta (comportamiento de accreditation-list)
   * - 'top': Desplaza suavemente hacia el inicio de la ventana
   * - 'anchor': Desplaza hacia un selector CSS o elemento objetivo (scrollTarget)
   * - 'none': No realiza ninguna acción de scroll
   */
  @Input() scrollBehavior: 'preserve' | 'top' | 'anchor' | 'none' = 'preserve';

  /** Selector CSS o Elemento DOM a enfocar cuando scrollBehavior === 'anchor' */
  @Input() scrollTarget?: string | HTMLElement;

  /** Emite el número de la nueva página seleccionada */
  @Output() pageChange = new EventEmitter<number>();

  get computedTotalPages(): number {
    if (this.totalPages !== undefined && this.totalPages > 0) {
      return this.totalPages;
    }
    if (this.totalItems !== undefined && this.pageSize > 0) {
      return Math.max(Math.ceil(this.totalItems / this.pageSize), 1);
    }
    return 1;
  }

  /**
   * Genera el listado de botones. Si hay más páginas que maxVisiblePages,
   * inserta -1 como centinela para renderizar '…'.
   */
  get visiblePages(): number[] {
    const total = this.computedTotalPages;
    const current = this.page;
    const max = this.maxVisiblePages;

    if (total <= max) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const pages: number[] = [];
    pages.push(1);

    const sidePages = 1;
    const startPage = Math.max(2, current - sidePages);
    const endPage = Math.min(total - 1, current + sidePages);

    if (startPage > 2) {
      pages.push(-1); // Elipsis izquierda
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    if (endPage < total - 1) {
      pages.push(-1); // Elipsis derecha
    }

    pages.push(total);
    return pages;
  }

  goToPage(newPage: number): void {
    if (
      newPage < 1 ||
      newPage > this.computedTotalPages ||
      newPage === this.page
    ) {
      return;
    }

    const scrollY = typeof window !== 'undefined' ? window.scrollY : 0;
    this.page = newPage;
    this.pageChange.emit(this.page);

    this.handleScroll(scrollY);
  }

  previousPage(): void {
    this.goToPage(this.page - 1);
  }

  nextPage(): void {
    this.goToPage(this.page + 1);
  }

  private handleScroll(previousScrollY: number): void {
    if (typeof window === 'undefined') return;

    requestAnimationFrame(() => {
      switch (this.scrollBehavior) {
        case 'preserve':
          window.scrollTo({ top: previousScrollY, behavior: 'auto' });
          break;
        case 'top':
          window.scrollTo({ top: 0, behavior: 'smooth' });
          break;
        case 'anchor':
          if (this.scrollTarget) {
            const el =
              typeof this.scrollTarget === 'string'
                ? document.querySelector(this.scrollTarget)
                : this.scrollTarget;
            el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
          break;
        case 'none':
        default:
          break;
      }
    });
  }
}
